import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { PatronBulkExtendLoans } from "@modules/Patron/PatronBulkExtendLoans";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import {
  Container,
  Divider,
  Grid,
  Header,
  Ref,
  Segment,
  Sticky,
  Statistic,
  Message,
} from "semantic-ui-react";
import { ItemsCheckout } from "./ItemsCheckout";
import { ItemsSearch } from "./ItemsSearch";
import { PatronActionMenu } from "./PatronActionMenu";
import { PatronCurrentLoans } from "./PatronCurrentLoans";
import { PatronHeader } from "./PatronHeader";
import { checkBorrowingLimit } from "@utils/CirculationUtils";
import { PatronPendingLoans } from "./PatronPendingLoans";
import { PatronPastLoans } from "./PatronPastLoans";
import { sessionManager } from "@authentication/services/SessionManager";

export default class PatronDetails extends Component {
  constructor(props) {
    super(props);
    this.menuRef = React.createRef();
    this.headerRef = React.createRef();
    // Add state for borrowing stats
    this.state = {
      borrowingStats: null,
      isLoadingStats: false,
    };
  }

  componentDidMount() {
    const { fetchPatronDetails, match } = this.props;
    fetchPatronDetails(match.params.patronPid);
    // Try to fetch borrowing stats
    this.fetchBorrowingStats();
  }

  componentDidUpdate(prevProps) {
    const { fetchPatronDetails, match, data, isLoading } = this.props;

    const currentPatronPid = match.params.patronPid;
    const samePatronPidFromRouter =
      prevProps.match.params.patronPid === currentPatronPid;
    if (!samePatronPidFromRouter) {
      fetchPatronDetails(currentPatronPid);
    }

    // Check if data was loading before and now it's loaded, or if patron changed
    if (
      (prevProps.isLoading && !isLoading) ||
      (prevProps.data && data && prevProps.data.pid !== data.pid)
    ) {
      this.fetchBorrowingStats();
    }
  }

  // Add method to fetch borrowing stats
  fetchBorrowingStats = async () => {
    const { data, isLoading } = this.props;

    // Only fetch if we have patron data and it's not loading
    if (isLoading || !data || !data.pid) return;

    this.setState({ isLoadingStats: true });

    try {
      const stats = await checkBorrowingLimit(
        data.pid,
        data.patron_type || "PATRON"
      );

      this.setState({
        borrowingStats: stats,
        isLoadingStats: false,
      });
    } catch (error) {
      console.error("Error fetching borrowing stats:", error);
      this.setState({ isLoadingStats: false });
    }
  };

  // Add method to render borrowing stats
  renderBorrowingStats = () => {
    const { borrowingStats, isLoadingStats } = this.state;
    if (isLoadingStats) return <Loader isLoading />;
    if (!borrowingStats) return null;

    const limitExceeded = !borrowingStats.canBorrow;

    return (
      <Segment>
        <Header as="h3">Borrowing Limits</Header>
        <Grid columns={3} divided>
          <Grid.Row>
            <Grid.Column>
              <Statistic>
                <Statistic.Value>{borrowingStats.current}</Statistic.Value>
                <Statistic.Label>Items Borrowed</Statistic.Label>
              </Statistic>
            </Grid.Column>
            <Grid.Column>
              <Statistic>
                <Statistic.Value>{borrowingStats.limit}</Statistic.Value>
                <Statistic.Label>Maximum Allowed</Statistic.Label>
              </Statistic>
            </Grid.Column>
            <Grid.Column>
              <Statistic color={limitExceeded ? "red" : "green"}>
                <Statistic.Value>{borrowingStats.remaining}</Statistic.Value>
                <Statistic.Label>Remaining</Statistic.Label>
              </Statistic>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {limitExceeded && (
          <Message warning>
            <Message.Header>Borrowing Limit Reached</Message.Header>
            <p>
              This patron has reached their borrowing limit and cannot check out
              additional items.
            </p>
          </Message>
        )}
      </Segment>
    );
  };

  render() {
    const { isLoading, error, data, match, currentLoans } = this.props;

    const currentPatronPid = match.params.patronPid;
    return (
      <div ref={this.headerRef}>
        <Container fluid>
          <Loader isLoading={isLoading}>
            <Error error={error}>
              <Sticky context={this.headerRef} className="solid-background">
                <Container fluid className="spaced">
                  <PatronHeader data={data} currentUser={sessionManager.user} />
                </Container>
                <Divider />
              </Sticky>
              <Container fluid>
                <Ref innerRef={this.menuRef}>
                  <Grid columns={2}>
                    <Grid.Column width={13}>
                      <Container className="spaced">
                        <Overridable
                          id="Backoffice.PatronDetails.Metadata"
                          patron={data}
                        />

                        {/* Add borrowing stats display */}
                        {this.renderBorrowingStats()}

                        <Header attached="top" as="h3">
                          Checkout
                        </Header>
                        <Segment
                          attached
                          className="bo-metadata-segment"
                          id="patron-checkout"
                        >
                          <ItemsCheckout />
                          <ItemsSearch patronData={data} />
                        </Segment>

                        <Header attached="top" as="h3">
                          Ongoing loans
                        </Header>
                        <Segment
                          attached
                          id="ongoing-loans"
                          className="bo-metadata-segment no-padding"
                        >
                          <PatronCurrentLoans />
                        </Segment>

                        <Header attached="top" as="h3">
                          Pending loans requests
                        </Header>
                        <Segment
                          attached
                          id="loan-requests"
                          className="bo-metadata-segment no-padding"
                        >
                          <PatronPendingLoans />
                        </Segment>

                        <Header attached="top" as="h3">
                          Loans history
                        </Header>
                        <Segment
                          attached
                          id="loans-history"
                          className="bo-metadata-segment no-padding"
                          style={{ minHeight: "300px" }}
                        >
                          <PatronPastLoans />
                        </Segment>
                      </Container>
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Sticky context={this.menuRef} offset={150}>
                        <PatronBulkExtendLoans
                          patronPid={currentPatronPid}
                          hidden={currentLoans.total === 0}
                          fluid
                          color="blue"
                        />
                        <PatronActionMenu offset={-500} />
                      </Sticky>
                    </Grid.Column>
                  </Grid>
                </Ref>
              </Container>
            </Error>
          </Loader>
        </Container>
      </div>
    );
  }
}

PatronDetails.propTypes = {
  fetchPatronDetails: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  error: PropTypes.object,
  isLoading: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      patronPid: PropTypes.string,
    }),
  }).isRequired,
  currentLoans: PropTypes.object.isRequired,
};

PatronDetails.defaultProps = {
  error: null,
  isLoading: false,
};
